import { useEffect, useRef, useState } from "react";
import { AlertTriangle, X } from "lucide-react";

interface DeleteVMConfirmModalProps {
  open: boolean;
  vmName: string;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
}

/**
 * Two-step confirmation before soft-deleting a VM. The user must
 * retype the VM name exactly so a misclick on the trash icon never
 * triggers a real action.
 */
export function DeleteVMConfirmModal({
  open,
  vmName,
  onClose,
  onConfirm,
  loading = false,
}: DeleteVMConfirmModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [typedName, setTypedName] = useState("");

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (open) {
      dialog.showModal();
      setTypedName("");
    } else {
      dialog.close();
    }
  }, [open]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    const handleClose = () => onClose();
    dialog.addEventListener("close", handleClose);
    return () => dialog.removeEventListener("close", handleClose);
  }, [onClose]);

  if (!open) return null;

  const matches = typedName === vmName;

  return (
    <dialog
      ref={dialogRef}
      className="fixed inset-0 z-50 m-auto max-w-md rounded-xl border border-gray-700 bg-gray-800 p-0 text-gray-100 shadow-2xl backdrop:bg-black/60"
      onClick={(e) => {
        if (e.target === dialogRef.current) onClose();
      }}
    >
      <div className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-yellow-400" />
            <h3 className="text-lg font-semibold">Move VM to trash</h3>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-700 hover:text-gray-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-4 space-y-3 text-sm text-gray-300">
          <p>
            This will <strong>stop</strong> <code className="rounded bg-gray-900 px-1 py-0.5 text-yellow-300">{vmName}</code>{" "}
            on Proxmox and move the entry to the trash. The VM disk, IP
            and HA replica are kept intact and you can restore the VM
            from <code className="rounded bg-gray-900 px-1 py-0.5">/vms/trash</code>{" "}
            for the next 30 days.
          </p>
          <p>
            To confirm, retype the VM name below:
          </p>
          <input
            type="text"
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            placeholder={vmName}
            autoFocus
            className="w-full rounded-lg border border-gray-600 bg-gray-900 px-3 py-2 font-mono text-sm text-gray-100 placeholder:text-gray-600 focus:border-yellow-400 focus:outline-none"
          />
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="rounded-lg border border-gray-600 bg-gray-700 px-4 py-2 text-sm font-medium text-gray-200 transition-colors hover:bg-gray-600 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!matches || loading}
            className="rounded-lg bg-yellow-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-yellow-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {loading ? "Moving…" : "Move to trash"}
          </button>
        </div>
      </div>
    </dialog>
  );
}
